import React, {ChangeEvent, useCallback, useContext, useEffect, useState} from "react";
import "./PaymentModal.scss"
import {FullScreenModal} from "../Modal/FullScreenModal";
import {AmountInput} from "./inputs/AmountInput/AmountInput";
import {CardconnectCreditCardInput} from "./inputs/CardconnectCreditCardInput/CardconnectCreditCardInput";
import {AutoExpandableTextarea} from "../AutoExpandableTextarea/AutoExpandableTextarea";
import {Invoice} from "../../models/invoice.model";
import {InvoiceContext} from "../../context/InvoiceContext";
import {CvvInput} from "./inputs/CvvInput/CvvInput";
import {ZipCodeInput} from "./inputs/ZipCodeInput/ZipCodeInput";
import {MonthExpiryInput} from "./inputs/MonthExpiryInput/MonthExpiryInput";
import {YearExpiryInput} from "./inputs/YearExpiryInput/YearExpiryInput";
import {Button} from "../ui-components/Button/Button";
import {ToastService} from "../../service/ToastService";
import {
    createAchPaymentAsync,
    createPaymentAsync,
    getPayrixTokenAsync,
    getPayrixUSTokenAsync
} from "../../api/PaymentApi";
import {PageContext} from "../../context/PageContext";
import {PreloaderContext} from "../../context/PreloaderContext";
import closeIcon from "../../assets/icons/times-solid.svg";
import {Company} from "../../models/company.model";
import {CompanyContext} from "../../context/CompanyContext";
import {CardholderNameInput} from "./inputs/CardholderNameInput/CardholderNameInput";
import {PayrixCreditCardInput} from "./inputs/PayrixCreditCardInput/PayrixCreditCardInput";
import {AchSwitcher, PAYMENT_METHOD_TYPE} from "./inputs/AchSwitcher/AchSwitcher";
import {AchForm} from "./AchForm/AchForm";
import {AchPayment} from "../../models/ach.payment.model";
import {CreditCardPayment} from "../../models/payment";
import {OptionContext} from "../../context/OptionContext";
import {BankOfAmericaContext, BankOfAmericaFlexMicroform} from "../../context/BankOfAmericaContext";
import {getBankOfAmericaCaptureContext} from "../../api/BankOfAmericaApi";
import {BankOfAmericaCVVInput} from "./inputs/BankOfAmericaCVVInput/BankOfAmericaCVVInput";
import {BankOfAmericaCreditCardInput} from "./inputs/BankOfAmericaCreditCardInput/BankOfAmericaCreditCardInput";
import {AddressInput} from "./inputs/AddressInput/AddressInput";
import {LocalityInput} from "./inputs/LocalityInput/LocalityInput";
import {StateInput} from "./inputs/StateInput/StateInput";
import {EmailInput} from "./inputs/EmailInput/EmailInput";
import {RainforestForm} from "./inputs/RainforestForm/RainforestForm";
import {RainforestFormElement} from "../../context/RainforestContext";
import {FullstackTokenizer} from "../../context/FullstackContext";
import {FullstackForm} from "./inputs/FullstackForm/FullstackForm";
import {NmiContext} from "../../context/NmiContext";
import {NmiForm} from "./inputs/NmiForm/NmiForm";
import {convertFloatToFormattedPrice, convertFormattedPriceToFloat} from "../../utils/calculate-invoice-summary.util";
import {useCardFeeRecoveryContext} from "../../context/CardFeeRecoveryContext";
import {RainforestPaymentMethodEnum} from "../../enums/rainforest-payment-method.enum";

interface PaymentModalProps {
    onCloseModal: () => void;
    onSubmitModal?: (pdf_url: string) => void;
    onlySubmit?: boolean;
    onSubmitCreditCardPaymentInformation?: (creditCard: CreditCardPayment) => void;
    onSubmitAchPaymentInformation?: (ach: AchPayment) => void;
}

export const PaymentModal: React.FunctionComponent<PaymentModalProps> = (props) => {
    const cardFeeRecovery = useCardFeeRecoveryContext();
    const invoice: Invoice = useContext(InvoiceContext);
    const company: Company = useContext(CompanyContext);
    const optionContext = useContext(OptionContext);
    const bankOfAmericaContext = useContext(BankOfAmericaContext);
    const nmiContext = useContext(NmiContext);
    const [amount, setAmount] = useState<string>(optionContext.getPaymentAmount());
    const [isAmountValid, setIsAmountValid] = useState<boolean>(false);
    const [creditCardNumber, setCreditCardNumber] = useState<string>('');
    const [isCreditCardNumberValid, setIsCreditCardNumberValid] = useState<boolean>(false);
    const [paymentNotes, setPaymentNotes] = useState<string>('');
    const [cvv, setCvv] = useState<string>('');
    const [isCvvValid, setIsCvvValid] = useState<boolean>(false);
    const [zipCode, setZipCode] = useState<string>('');
    const [isZipCodeValid, setIsZipCodeValid] = useState<boolean>(false);
    const [monthExpiry, setMonthExpiry] = useState<string>('');
    const [isMonthExpiryValid, setIsMonthExpiryValid] = useState<boolean>(false);
    const [yearExpiry, setYearExpiry] = useState<string>('');
    const [isYearExpiryValid, setIsYearExpiryValid] = useState<boolean>(false);
    const maxAmount = optionContext.getPaymentAmount();
    const [isYearSelected, setIsYearSelected] = useState<boolean>(false);
    const [isCvvSelected, setIsCvvSelected] = useState<boolean>(false);
    const [cardholderName, setCardholderName] = useState<string>('');
    const [isCardholderNameValid, setIsCardholderNameValid] = useState<boolean>(false);
    const [paymentMethodType, _setPaymentMethodType] = useState<PAYMENT_METHOD_TYPE>(PAYMENT_METHOD_TYPE.CREDIT_CARD);
    const [isFullstackInited, setFullstackInited] = useState<boolean>(false);
    const [isRainforestInited, setRainforestInited] = useState<boolean>(false);
    const [achPayment, setAchPayment] = useState<AchPayment | null>(null);
    const [isAchPaymentValid, setAchPaymentValid] = useState<boolean>(false);
    const [bankOfAmericaFlexMicroform, setBankOfAmericaFlexMicroform] = useState<BankOfAmericaFlexMicroform | null>(null);
    const [email, setEmail] = useState<string>('');
    const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
    const [address, setAddress] = useState<string>('');
    const [isAddressValid, setIsAddressValid] = useState<boolean>(false);
    const [locality, setLocality] = useState<string>('');
    const [isLocalityValid, setIsLocalityValid] = useState<boolean>(false);
    const [state, setState] = useState<string>('');
    const [isStateValid, setIsStateValid] = useState<boolean>(false);
    const [rainforestForm, setRainforestForm] = useState<RainforestFormElement | null>(null);
    const [isRainforestValid, setRainforestValid] = useState<boolean>(false);
    const [isRainforestFormLoading, setIsRainforestFormLoading] = useState<boolean>(false);
    const [rainforestPaymentMethod, setRainforestPaymentMethod] = useState<RainforestPaymentMethodEnum | null>(null);
    const [rainforestPayinId, setRainforestPayinId] = useState<string>('');
    const [rainforestPayinConfigId, setRainforestPayinConfigId] = useState<string>('');
    const [fullstackToken, setFullstackToken] = useState<string>('');
    const [fullstackTokenizer, setFullstackTokenizer] = useState<FullstackTokenizer | null>(null);
    const [isFullstackValid, setFullstackValid] = useState<boolean>(false);
    const [nmiToken, setNmiToken] = useState<string>('');
    const [isNmiValid, setNmiValid] = useState<boolean>(false);
    const pageContext = useContext(PageContext);
    const preloaderContext = useContext(PreloaderContext);
    let isSendingPaymentRequest = false;

    useEffect(() => {
        if (company.is_payrix_enabled) {
            setIsZipCodeValid(true);
        }
        if (company.is_bank_of_america_enabled) {
            initBankOfAmerica();
        }
    }, [company]);

    // Auto-submit Rainforest payment when Payin is approved
    useEffect(() => {
        if (rainforestPayinId && rainforestPayinConfigId) {
            onSubmitPaymentForm(null);
        }
    }, [rainforestPayinId, rainforestPayinConfigId]);

    // Auto-submit Fullstack payment when Fullstack token is received
    useEffect(() => {
        if (fullstackToken) {
            onSubmitPaymentForm(null);
        }
    }, [fullstackToken]);

    // Auto-submit NMI payment when token is received from the NmiPayments widget
    useEffect(() => {
        if (nmiToken) {
            onSubmitPaymentForm(null);
        }
    }, [nmiToken]);

    useEffect(() => {
        setAchPaymentValid(isFullstackValid);
    }, [isFullstackValid])

    const onSetCreditCardNumber = (creditCardNumber: string) => {
        setCreditCardNumber(creditCardNumber);
    }

    const onSetPaymentNotes = (event: ChangeEvent) => {
        setPaymentNotes((event.target as HTMLTextAreaElement).value);
    }

    const onSetIsMonthExpiryValid = (isMonthExpiryValid: boolean) => {
        setIsMonthExpiryValid(isMonthExpiryValid);
        setIsYearSelected(isMonthExpiryValid);
    }

    const onSetIsYearExpiryValid = (isYearExpiryValid: boolean) => {
        setIsYearExpiryValid(isYearExpiryValid);
        setIsCvvSelected(isYearExpiryValid);
    }

    const isFormValid = () => {
        if (company.is_bank_of_america_enabled) {
            return isAmountValid && isMonthExpiryValid && isYearExpiryValid && isZipCodeValid && isCardholderNameValid && isEmailValid && isAddressValid && isLocalityValid && isStateValid;
        }

        if (company.is_rainforest_enabled) {
            if (rainforestPaymentMethod === RainforestPaymentMethodEnum.ApplePay) {
                return true;
            }

            return isRainforestValid;
        }

        if (company.is_fullstack_enabled) {
            return isAmountValid && isZipCodeValid && isCardholderNameValid && (isFullstackValid || paymentMethodType === PAYMENT_METHOD_TYPE.ACH);
        }

        if (company.is_nmi_enabled) {
            return isAmountValid && isNmiValid && Boolean(nmiContext.tokenizationKey);
        }

        return isCreditCardNumberValid && isAmountValid && isCvvValid && isMonthExpiryValid && isYearExpiryValid && isZipCodeValid && (!company.is_payrix_enabled || isCardholderNameValid);
    }

    const canBePaidWithAch = useCallback(() => {
        return company.is_payrix_us_enabled || company.is_cardconnect_enabled || company.is_fullstack_enabled || company.is_rainforest_enabled;
    }, [company]);

    const isAchAvailable = useCallback(() => {
        return canBePaidWithAch() && invoice.is_ach_payment_available;
    }, [company]);

    const isCreditCardAvailable = useCallback(() => {
        if (company.is_rainforest_enabled) {
            return invoice.is_credit_card_payment_available;
        }

        // Don't change this condition, because it covers some legacy or edge case
        return !canBePaidWithAch() || invoice.is_credit_card_payment_available || !isAchAvailable();
    }, [company]);

    const isCreditCardPaymentMethodType = useCallback(() => {
        return paymentMethodType === PAYMENT_METHOD_TYPE.CREDIT_CARD && !company.is_rainforest_enabled && !company.is_fullstack_enabled && !company.is_nmi_enabled;
    }, [paymentMethodType]);

    const isAchPaymentMethodType = useCallback(() => {
        return paymentMethodType === PAYMENT_METHOD_TYPE.ACH && !company.is_rainforest_enabled && !company.is_fullstack_enabled && !company.is_nmi_enabled;
    }, [paymentMethodType]);

    useEffect(() => {
        if (invoice && company) {
            setFullstackInited(true);
            if (isAchAvailable() && !isCreditCardAvailable()) {
                updatePaymentMethodType(PAYMENT_METHOD_TYPE.ACH);
            }
            if (company.is_rainforest_enabled && !invoice.is_credit_card_payment_available && invoice.is_ach_payment_available) {
                updatePaymentMethodType(PAYMENT_METHOD_TYPE.ACH);
            }
            setRainforestInited(true);
        }
    }, [invoice, company]);

    const onSubmitPaymentForm = async (event: React.MouseEvent | null) => {
        if (event) {
            event.preventDefault();
        }
        if (isAchPaymentMethodType()) {
            onSubmitAchPaymentForm();
            return;
        }
        if (isFormValid()) {
            let creditCard = creditCardNumber;
            preloaderContext.showMiddlePagePreloader();
            if (company.is_payrix_enabled) {
                const payrixResponse = await getPayrixTokenAsync(company.payrix_token_api_url, {
                    zipCode,
                    cvv,
                    creditCardNumber,
                    month: monthExpiry,
                    year: yearExpiry,
                    cardholderName: cardholderName
                }).catch(() => {

                });
                if (payrixResponse?.token) {
                    creditCard = payrixResponse?.token;
                } else {
                    preloaderContext.hideMiddlePagePreloader();
                    if (payrixResponse?.errorMessage) {
                        ToastService.middleScreenErrorToast(payrixResponse?.errorMessage);
                    } else {
                        ToastService.middleScreenErrorToast('Please try again or contact FieldPulse support.');
                    }
                    return;
                }
            }
            if (company.is_payrix_us_enabled) {
                const payrixResponse = await getPayrixUSTokenAsync(company.payrix_us_token_api_url, company.payrix_us_public_api_token, company.payrix_us_merchant_id, {
                    zipCode,
                    cvv,
                    creditCardNumber,
                    month: monthExpiry,
                    year: yearExpiry,
                    cardholderName: cardholderName
                }).catch(() => {

                });
                if (payrixResponse?.response?.data[0]?.token) {
                    creditCard = payrixResponse?.response?.data[0]?.token;
                } else {
                    preloaderContext.hideMiddlePagePreloader();
                    if (payrixResponse?.errors) {
                        ToastService.middleScreenErrorToast(payrixResponse?.errors);
                    } else {
                        ToastService.middleScreenErrorToast('Please try again or contact FieldPulse support.');
                    }
                    return;
                }
            }
            if (company.is_bank_of_america_enabled) {
                const token = await getBankOfAmericaToken();
                if (token) {
                    creditCard = token as string;
                } else {
                    preloaderContext.hideMiddlePagePreloader();
                    return;
                }
            }
            if (company.is_rainforest_enabled && !rainforestPayinId) {
                rainforestForm?.submit();
                return;
            }

            if (company.is_fullstack_enabled && !fullstackToken) {
                fullstackTokenizer?.submit();
                return;
            }

            if (company.is_nmi_enabled && !nmiToken) {
                preloaderContext.hideMiddlePagePreloader();
                ToastService.middleScreenErrorToast('Please complete the payment details.');
                return;
            }
            if (props.onlySubmit) {
                if (isAchPaymentMethodType() && props.onSubmitAchPaymentInformation) {
                    props.onSubmitAchPaymentInformation(achPayment as AchPayment);
                }
                if (!isAchPaymentMethodType() && props.onSubmitCreditCardPaymentInformation) {
                    props.onSubmitCreditCardPaymentInformation({
                        amount,
                        creditCardNumber: creditCard,
                        paymentNotes,
                        cvv,
                        zipCode,
                        monthExpiry,
                        yearExpiry,
                        cardholderName,
                    });
                }
                preloaderContext.hideMiddlePagePreloader();
                return;
            }
            const selectedAddOnOptionIds = optionContext.selectedAddOnOptions.map((option) => option.id);
            const apiType = paymentMethodType === PAYMENT_METHOD_TYPE.ACH ? 'ach' : 'card';
            const response = await createPaymentAsync({
                type: apiType,
                amount,
                zipCode,
                cvv,
                creditCardNumber: creditCard,
                month: monthExpiry,
                notes: paymentNotes,
                year: yearExpiry,
                holderName: cardholderName,
                email,
                address,
                locality,
                state,
                fingerprintSessionId: bankOfAmericaContext.fingerprintSessionId,
                rainforestPayinId,
                rainforestPayinConfigId,
                fullstackToken: fullstackToken as string,
                nmiToken: nmiToken as string,
            }, company.is_payrix_enabled, optionContext.selectedOption?.id, selectedAddOnOptionIds, company.is_nmi_enabled).catch(() => {
            });
            preloaderContext.hideMiddlePagePreloader();
            isSendingPaymentRequest = false;
            onPaymentResponse(response);
        } else {
            ToastService.middleScreenErrorToast('Please make sure that everything is filled correctly.');
        }
    }

    const onPaymentResponse = (response: any) => {
        if (response && !response.error) {
            pageContext.setPageData({
                pdf_url: response?.response?.pdf_url,
                paid_pdf_url: response?.response?.paid_pdf_url
            });
            pageContext.setPage(response?.response?.status);
            if (props.onSubmitModal) {
                props.onSubmitModal(response?.response?.pdf_url as string);
            }
        } else if (!response || (response && response.error)) {
            if (response && response.errors && response.errors.length) {
                ToastService.middleScreenErrorToast(response.errors.join('\n'));
            } else {
                ToastService.middleScreenErrorToast('Please try again or contact FieldPulse support.');
            }
        }
    }

    const onSubmitAchPaymentForm = async () => {
        if (isAchPaymentValid && achPayment) {
            preloaderContext.showMiddlePagePreloader();
            const selectedAddOnOptionIds = optionContext.selectedAddOnOptions.map((option) => option.id);
            const response = await createAchPaymentAsync(achPayment, optionContext.selectedOption?.id, selectedAddOnOptionIds).catch(() => {
            });
            preloaderContext.hideMiddlePagePreloader();
            isSendingPaymentRequest = false;
            onPaymentResponse(response);
        } else {
            ToastService.middleScreenErrorToast('Please make sure that everything is filled correctly.');
        }
    }

    const onClosePaymentForm = (event: React.MouseEvent) => {
        props.onCloseModal();
    }

    const initBankOfAmerica = () => {
        getBankOfAmericaCaptureContext()
            .then((context: any) => {
                const bankOfAmericaFlex = bankOfAmericaContext.getFlex(context['captureContext']);
                setBankOfAmericaFlexMicroform(bankOfAmericaFlex.microform({styles: bankOfAmericaContext.getInputStyles()}));
            })
    }

    const getBankOfAmericaToken = () => {
        return new Promise((resolve, reject) => {
            bankOfAmericaFlexMicroform?.createToken({
                expirationMonth: monthExpiry,
                expirationYear: new Date().getFullYear().toString().slice(0, 2) + yearExpiry
            }, (error, token) => {
                if (error) {
                    ToastService.middleScreenErrorToast(error.message);
                    reject();
                } else {
                    resolve(token);
                }
            })
        });
    }

    const onDeclineRainforest = () => {
        preloaderContext.hideMiddlePagePreloader();
    }

    const onApproveRainforest = (paymentMethod: RainforestPaymentMethodEnum, payinId: string, payinConfigId: string) => {
        preloaderContext.hideMiddlePagePreloader();
        setRainforestPaymentMethod(paymentMethod);
        setRainforestPayinConfigId(payinConfigId);
        setRainforestPayinId(payinId);
    }

    const onApproveFullstack = (token: string) => {
        setFullstackToken(token);
    }

    const updatePaymentMethodType = (method: PAYMENT_METHOD_TYPE) => {
        _setPaymentMethodType(method);

        // When Card Fee Recovery is enabled, we provide user with two prices for each payment type: Card and ACH
        // So, when user changes the payment type, we automatically toggle the price for them
        if (cardFeeRecovery.isEnabled) {
            if (method === PAYMENT_METHOD_TYPE.ACH) {
                setAmount(optionContext.getPaymentAmountWithoutCardFeeRecovery());
            } else {
                setAmount(optionContext.getPaymentAmount());
            }
        }
    }

    const handleNmiFormApprovePayment = (token: string, paymentMethod: PAYMENT_METHOD_TYPE) => {
        setNmiToken(token);
        updatePaymentMethodType(paymentMethod);
    };

    const renderCardconnectContent = () => {
        return (
            <>
                <header>
                    <h1>Pay Now</h1>

                    <img onClick={onClosePaymentForm} className='image-icon desktop-2x clickable' src={closeIcon}
                         alt="close"/>
                </header>

                <main className='payment-modal'>
                    {isCreditCardAvailable() && isAchAvailable() && !company.is_rainforest_enabled &&
                        <AchSwitcher currentType={paymentMethodType} onSetPaymentMethodType={updatePaymentMethodType}/>}
                    {isCreditCardPaymentMethodType() && <form>
                        <div className="form-group amount-and-card-number-container">
                            <div className='input-container amount-input-container'>
                                <label>Amount ({company.currency_code}):</label>
                                <AmountInput amount={amount} maxAmount={maxAmount} onSetAmount={setAmount}
                                             onSetIsAmountValid={setIsAmountValid}/>
                            </div>

                            {(company.is_payrix_enabled || company.is_payrix_us_enabled) &&
                                <div className='input-container card-number-input-container'>
                                    <label>Card Number:</label>
                                    <PayrixCreditCardInput creditCard={creditCardNumber}
                                                           onSetCreditCard={onSetCreditCardNumber}
                                                           onSetIsCreditCardValid={setIsCreditCardNumberValid}/>
                                </div>}
                            {company.is_cardconnect_enabled &&
                                <div className='input-container card-number-input-container'>
                                    <label>Card Number:</label>
                                    <CardconnectCreditCardInput onSetIsCreditCardNumber={setIsCreditCardNumberValid}
                                                                onSetCreditCardNumber={onSetCreditCardNumber}/>
                                </div>}

                            {company.is_bank_of_america_enabled &&
                                <div className='input-container card-number-input-container'>
                                    <label>Card Number:</label>
                                    <BankOfAmericaCreditCardInput
                                        bankOfAmericaFlexMicroform={bankOfAmericaFlexMicroform as BankOfAmericaFlexMicroform}/>
                                </div>}

                        </div>

                        {company.is_payrix_enabled || company.is_payrix_us_enabled || company.is_bank_of_america_enabled ?
                            <div className="form-group amount-and-card-number-container">
                                <div className='input-container amount-input-container'>
                                    <label>Cardholder Name:</label>
                                    <CardholderNameInput cardholderName={cardholderName}
                                                         onSetCardholderName={setCardholderName}
                                                         onSetIsCardholderNameValid={setIsCardholderNameValid}/>
                                </div>
                            </div> : null}

                        <div className="form-group card-data-container">
                            <div className='input-container'>
                                <label>Expiry Month:</label>
                                <MonthExpiryInput monthExpiry={monthExpiry} onSetMonthExpiry={setMonthExpiry}
                                                  onSetMonthExpiryValid={onSetIsMonthExpiryValid}/>
                            </div>

                            <div className='input-container'>
                                <label>Expiry Year:</label>
                                <YearExpiryInput isYearSelected={isYearSelected} yearExpiry={yearExpiry}
                                                 onSetYearExpiry={setYearExpiry}
                                                 onSetYearExpiryValid={onSetIsYearExpiryValid}/>
                            </div>

                            {!company.is_bank_of_america_enabled && <div className='input-container'>
                                <label>CVV:</label>
                                <CvvInput isCvvSelected={isCvvSelected} cvv={cvv} onSetCvv={setCvv}
                                          onSetCvvValid={setIsCvvValid}/>
                            </div>}

                            {company.is_bank_of_america_enabled && <div className='input-container'>
                                <label>CVV:</label>
                                <BankOfAmericaCVVInput
                                    bankOfAmericaFlexMicroform={bankOfAmericaFlexMicroform as BankOfAmericaFlexMicroform}/>
                            </div>}

                            {!company.is_payrix_enabled ? <div className='input-container'>
                                <label>ZIP:</label>
                                <ZipCodeInput zipCode={zipCode} onSetZipCode={setZipCode}
                                              onSetZipCodeValid={setIsZipCodeValid}/>
                            </div> : null}
                        </div>

                        {company.is_bank_of_america_enabled && <div className="form-group card-data-container">
                            <div className='input-container'>
                                <label>Address:</label>
                                <AddressInput address={address} onSetAddress={setAddress}
                                              onSetIsAddressValid={setIsAddressValid}/>
                            </div>

                            <div className='input-container'>
                                <label>City:</label>
                                <LocalityInput locality={locality} onSetLocality={setLocality}
                                               onSetIsLocalityValid={setIsLocalityValid}/>
                            </div>

                            <div className='input-container'>
                                <label>State:</label>
                                <StateInput state={state} onSetIsStateValid={setIsStateValid}
                                            onSetState={setState}></StateInput>
                            </div>

                            <div className='input-container'>
                                <label>Email:</label>
                                <EmailInput email={email} onSetEmail={setEmail} onSetIsEmailValid={setIsEmailValid}/>
                            </div>
                        </div>}

                        <div className='form-group'>
                            <div className='input-container'>
                                <label>Payment Notes:</label>
                                <AutoExpandableTextarea value={paymentNotes} initRows={3} maxRows={5}
                                                        onChange={onSetPaymentNotes}/>
                            </div>
                        </div>
                    </form>}
                    {isAchPaymentMethodType() &&
                        <AchForm onSetAchPayment={setAchPayment} onSetAchPaymentValid={setAchPaymentValid}/>}

                    {company.is_rainforest_enabled && isRainforestInited && <RainforestForm amount={amount} isAchAvailable={isAchAvailable()}
                                                                      paymentMethod={paymentMethodType}
                                                                      isCreditCardAvailable={isCreditCardAvailable()}
                                                                      setRainforestForm={setRainforestForm}
                                                                      onToggleValid={setRainforestValid}
                                                                      onChangeLoadingState={setIsRainforestFormLoading}
                                                                      onDeclinedPayment={onDeclineRainforest}
                                                                      onApprovedPayment={onApproveRainforest}
                                                                      onChangePaymentMethod={updatePaymentMethodType}
                    />}

                    {company.is_fullstack_enabled && isFullstackInited &&
                        <FullstackForm amount={amount} isAchAvailable={isAchAvailable()}
                                       paymentMethodType={paymentMethodType}
                                       isCreditCardAvailable={isCreditCardAvailable()}
                                       setFullstackTokenizer={setFullstackTokenizer}
                                       onApprovedPayment={onApproveFullstack}
                                       cardholderName={cardholderName}
                                       setCardholderName={setCardholderName}
                                       setIsCardholderNameValid={setIsCardholderNameValid}
                                       zipCode={zipCode} setZipCode={setZipCode}
                                       setIsZipCodeValid={setIsZipCodeValid}
                                       setAmount={setAmount}
                                       setIsAmountValid={setIsAmountValid}
                                       onSetValidFullstack={setFullstackValid}/>}

                    {company.is_nmi_enabled && nmiContext.tokenizationKey &&
                        <NmiForm tokenizationKey={nmiContext.tokenizationKey}
                                 amount={amount}
                                 setAmount={setAmount}
                                 setIsAmountValid={setIsAmountValid}
                                 onApprovedPayment={handleNmiFormApprovePayment}
                                 onSetValid={setNmiValid}
                                 isCreditCardAvailable={invoice.is_credit_card_payment_available}
                                 isAchAvailable={invoice.is_ach_payment_available}
                                 onFieldsAvailable={() => {}}/>}
                </main>

                <footer>
                    <Button className='secondary-button' onClick={onClosePaymentForm} type={'button'}>Close</Button>
                    <Button className='payment-submit-button'
                            disabled={isRainforestFormLoading || (company.is_nmi_enabled && !nmiContext.tokenizationKey)}
                            onClick={onSubmitPaymentForm}
                            type={'submit'}>{company.is_rainforest_enabled
                        ? `Pay ${convertFloatToFormattedPrice(convertFormattedPriceToFloat(amount), company.currency_sign)}`
                        : 'Submit'}</Button>

                    <div className='terms-container'>
                        <p className='terms-text'>
                            <a target='_blank'
                               href='https://fieldpulse.com/payments-terms-and-policies'>Terms and
                                Policies</a>
                        </p>
                    </div>
                </footer>
            </>
        )
    }

    return <FullScreenModal>
        {renderCardconnectContent()}
    </FullScreenModal>

}
